const COLLECTION_KINDS = Object.freeze([
  'events',
  'types',
  'categories',
  'actions',
  'rules',
  'queue',
]);
const BINDING_PREFIX_PATTERN = /^domain_[a-z0-9]+(?:_[a-z0-9]+)*$/;
const TOP_LEVEL_COLLECTION_KEYS = Object.freeze({
  events: 'eventsCollection',
  types: 'typesCollection',
  categories: 'categoriesCollection',
  actions: 'actionsCollection',
  rules: 'rulesCollection',
  queue: 'queueCollection',
});

export const DOMAIN_COLLECTION_BINDING_RESERVATIONS = 'domainCollectionBindings';

export function getDomainCollectionReservationId(schoolId, bindingId) {
  if (!schoolId || !bindingId) {
    throw new Error('A Domain collection reservation id requires school and binding ids');
  }
  return `${schoolId}__${bindingId}`;
}

function expectedCollections(bindingPrefix) {
  return Object.fromEntries(COLLECTION_KINDS.map((kind) => [
    kind,
    `${bindingPrefix}_${kind}`,
  ]));
}

function canonicalBindingForName(name) {
  if (typeof name !== 'string') return null;
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug ? `domain_${slug}` : null;
}

function assertBindingAlias(value, expected, label) {
  if (value !== undefined && value !== expected) {
    throw new Error(
      `Domain collection binding ${label} does not belong to ${expected}`,
    );
  }
}

function isReservationTimestamp(value) {
  return value instanceof Date
    || typeof value?.toMillis === 'function'
    || typeof value?.toDate === 'function'
    || (Number.isFinite(value?.seconds) && Number.isFinite(value?.nanoseconds));
}

/**
 * Return the stable reservation represented by one canonical dynamic Domain.
 * Non-dynamic legacy/adapter Domains intentionally return null because several
 * established central adapters share their historical collections.
 */
export function getDomainCollectionBinding(domain) {
  const collections = domain?.collections;
  const governanceBindings = domain?.governance?.bindings;
  if (collections !== undefined
    && (!collections || typeof collections !== 'object' || Array.isArray(collections))) {
    throw new Error('Domain collections binding must be an object');
  }
  if (governanceBindings !== undefined
    && (!governanceBindings
      || typeof governanceBindings !== 'object'
      || Array.isArray(governanceBindings))) {
    throw new Error('Domain governance bindings must be an object');
  }
  const governedCollections = governanceBindings?.collections;
  if (governedCollections !== undefined
    && (!governedCollections
      || typeof governedCollections !== 'object'
      || Array.isArray(governedCollections))) {
    throw new Error('Domain governance collection bindings must be an object');
  }

  const eventAliases = [
    collections?.events,
    domain?.eventsCollection,
    governedCollections?.events,
    governanceBindings?.eventsCollection,
  ].filter((value) => value !== undefined);
  if (eventAliases.some((value) => typeof value !== 'string')) {
    throw new Error('Domain events collection bindings must be strings');
  }
  const eventsCollection = eventAliases.find(
    (value) => typeof value === 'string' && value.startsWith('domain_'),
  );
  // Established central adapters intentionally use historical collections.
  if (!eventsCollection) {
    const expectsDynamicBinding = domain?.collectionBindingId !== undefined
      || domain?.runtime?.adapter === 'generic'
      || domain?.governance?.definitionAuthority === 'school';
    if (expectsDynamicBinding) {
      throw new Error('School-authored Domain events require a dynamic collection binding');
    }
    return null;
  }
  if (!eventsCollection.endsWith('_events')) {
    throw new Error(`Invalid dynamic Domain events collection: ${eventsCollection}`);
  }

  const bindingPrefix = eventsCollection.slice(0, -'_events'.length);
  if (!BINDING_PREFIX_PATTERN.test(bindingPrefix)) {
    throw new Error(`Invalid dynamic Domain collection binding: ${bindingPrefix}`);
  }
  const canonicalNameBinding = canonicalBindingForName(domain?.name);
  if (domain?.name !== undefined
    && canonicalNameBinding !== bindingPrefix) {
    throw new Error(
      `Domain collection binding ${bindingPrefix} does not belong to ${domain.name}`,
    );
  }
  if (domain?.collectionBindingId !== undefined
    && domain.collectionBindingId !== bindingPrefix) {
    throw new Error(
      `Domain collection binding id does not belong to ${eventsCollection}`,
    );
  }
  const expected = expectedCollections(bindingPrefix);
  for (const kind of COLLECTION_KINDS) {
    const topLevelKey = TOP_LEVEL_COLLECTION_KEYS[kind];
    assertBindingAlias(collections?.[kind], expected[kind], `collections.${kind}`);
    assertBindingAlias(domain?.[topLevelKey], expected[kind], topLevelKey);
    assertBindingAlias(
      governedCollections?.[kind],
      expected[kind],
      `governance.bindings.collections.${kind}`,
    );
    assertBindingAlias(
      governanceBindings?.[topLevelKey],
      expected[kind],
      `governance.bindings.${topLevelKey}`,
    );
  }

  const expectedRuleFires = `${bindingPrefix}_ruleFires`;
  assertBindingAlias(
    domain?.ruleFiresCollection,
    expectedRuleFires,
    'ruleFiresCollection',
  );
  assertBindingAlias(
    governanceBindings?.ruleFiresCollection,
    expectedRuleFires,
    'governance.bindings.ruleFiresCollection',
  );
  if (governanceBindings?.name !== undefined
    && governanceBindings.name !== domain?.name) {
    throw new Error(
      'Domain governance binding name does not match the Domain name',
    );
  }

  return {
    id: bindingPrefix,
    collections: expected,
    ruleFiresCollection: expectedRuleFires,
  };
}

export function buildDomainCollectionReservation({
  schoolId,
  domainId,
  binding,
  createdAt,
}) {
  if (!schoolId || !domainId || !binding?.id || createdAt == null) {
    throw new Error('A Domain collection reservation requires school, Domain, and binding ids');
  }
  return {
    version: 1,
    schoolId,
    domainId,
    bindingId: binding.id,
    createdAt,
  };
}

/**
 * Reservations are immutable. Any unexpected owner or shape is a hard stop;
 * an administrator must not silently repair or steal historical collections.
 */
export function assertDomainCollectionReservationOwner({
  reservation,
  schoolId,
  domainId,
  binding,
}) {
  const keys = reservation && typeof reservation === 'object'
    ? Object.keys(reservation)
    : [];
  if (!reservation || typeof reservation !== 'object'
    || keys.length !== 5
    || !['version', 'schoolId', 'domainId', 'bindingId', 'createdAt']
      .every((key) => keys.includes(key))
    || !isReservationTimestamp(reservation.createdAt)) {
    throw new Error(
      `Domain collection reservation ${binding?.id || ''} is malformed`,
    );
  }
  if (reservation.version !== 1
    || reservation.schoolId !== schoolId
    || reservation.domainId !== domainId
    || reservation.bindingId !== binding.id) {
    throw new Error(
      `Domain collections ${binding.id} are already reserved by another Domain`,
    );
  }
}
